/**
 * Area Intelligence Service
 * 
 * Provides traffic incidents, weather, and area context data
 */

export interface TrafficIncident {
  id: string;
  type: 'accident' | 'construction' | 'congestion' | 'road_closure' | 'weather' | 'other';
  severity: 'minor' | 'moderate' | 'major' | 'severe';
  title: string;
  description: string;
  latitude: number;
  longitude: number;
  road?: string;
  direction?: string;
  start_time?: string;
  end_time?: string;
  last_updated?: string;
  source: string;
  from_location?: string;
  to_location?: string;
  length_meters?: number;
  delay_seconds?: number;
  magnitude_of_delay?: number;
  icon_category?: number;
  event_code?: number;
  time_validity?: string;
  probability_of_occurrence?: string;
  number_of_reports?: number;
  geometry?: {
    type?: string;
    coordinates?: unknown;
  };
}

export interface WeatherData {
  temperature: number;
  temperatureUnit: string;
  windSpeed: string;
  windDirection: string;
  shortForecast: string;
  detailedForecast?: string;
  icon?: string;
  isDaytime: boolean;
}

export interface WeatherAlert {
  id: string;
  event: string;
  severity: string;
  urgency?: string;
  certainty?: string;
  headline: string;
  description: string;
  instruction?: string;
  area_description?: string;
  effective?: string;
  expires?: string;
  latitude?: number;
  longitude?: number;
  source: string;
}

export interface AreaIntelResponse {
  incidents: TrafficIncident[];
  incident_count: number;
  weather?: WeatherData;
  weather_alerts: WeatherAlert[];
  weather_alert_count: number;
  last_updated: string;
  source: string;
  bounds: {
    min_lat: number;
    min_lon: number;
    max_lat: number;
    max_lon: number;
  };
}

export interface AreaSummary {
  location: {
    latitude: number;
    longitude: number;
    radius_miles: number;
  };
  traffic: {
    total_incidents: number;
    by_type: Record<string, number>;
    by_severity: Record<string, number>;
    notable: Array<{
      title: string;
      type: string;
      severity: string;
      road?: string;
    }>;
  };
  weather?: WeatherData;
  generated_at: string;
}

// Mapbox token from environment
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

export interface NearbyPlace {
  name: string;
  category: string;
  address?: string;
  distance?: number; // meters
  latitude: number;
  longitude: number;
}

export interface LocationContext {
  address?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  nearbyPlaces: NearbyPlace[];
}

class AreaIntelService {
  /**
   * Get traffic incidents for a bounding box area
   */
  async getTrafficIncidents(
    bounds: { north: number; south: number; east: number; west: number },
    options?: { state?: string; includeWeather?: boolean; includeWeatherAlerts?: boolean; zoom?: number }
  ): Promise<AreaIntelResponse> {
    const params = new URLSearchParams({
      min_lat: bounds.south.toString(),
      max_lat: bounds.north.toString(),
      min_lon: bounds.west.toString(),
      max_lon: bounds.east.toString(),
      state: options?.state || 'AUTO',  // Auto-detect state from coordinates
      include_weather: (options?.includeWeather || false).toString(),
      include_weather_alerts: (options?.includeWeatherAlerts || false).toString()
    });

    if (typeof options?.zoom === 'number') {
      params.set('zoom', options.zoom.toString());
    }

    const response = await fetch(`/api/area-intel/traffic?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch traffic: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Get traffic incidents and NWS weather alerts for a map viewport.
   */
  async getAreaAlerts(
    bounds: { north: number; south: number; east: number; west: number },
    options?: { state?: string; includeWeather?: boolean; zoom?: number }
  ): Promise<AreaIntelResponse> {
    return this.getTrafficIncidents(bounds, {
      ...options,
      includeWeatherAlerts: true,
    });
  }

  /**
   * Get area summary for AI context
   */
  async getAreaSummary(
    lat: number, 
    lon: number, 
    radiusMiles: number = 5
  ): Promise<AreaSummary> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lon: lon.toString(),
      radius_miles: radiusMiles.toString()
    });

    const response = await fetch(`/api/area-intel/summary?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch area summary: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Reverse geocode a location to get address and context
   */
  async reverseGeocode(lat: number, lon: number): Promise<LocationContext> {
    const result: LocationContext = {
      nearbyPlaces: []
    };

    if (!MAPBOX_TOKEN) {
      console.warn('Mapbox token not configured');
      return result;
    }

    try {
      // Reverse geocode for address
      const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?types=address,neighborhood,locality,place,region&access_token=${MAPBOX_TOKEN}`;
      const geocodeResponse = await fetch(geocodeUrl);
      
      if (geocodeResponse.ok) {
        const geocodeData = await geocodeResponse.json();
        const features = geocodeData.features || [];
        
        for (const feature of features) {
          const placeType = feature.place_type?.[0];
          if (placeType === 'address') {
            result.address = feature.place_name;
          } else if (placeType === 'neighborhood') {
            result.neighborhood = feature.text;
          } else if (placeType === 'locality' || placeType === 'place') {
            result.city = feature.text;
          } else if (placeType === 'region') {
            result.state = feature.text;
          }
        }
      }
    } catch (e) {
      console.warn('Reverse geocode error:', e);
    }

    return result;
  }

  /**
   * Search for nearby points of interest
   */
  async getNearbyPOIs(
    lat: number, 
    lon: number, 
    categories?: string[],
    limit: number = 10
  ): Promise<NearbyPlace[]> {
    const places: NearbyPlace[] = [];

    if (!MAPBOX_TOKEN) {
      console.warn('Mapbox token not configured for POI search');
      return places;
    }

    // Define search categories - Mapbox Geocoding needs actual search terms
    const searchTerms = categories || ['restaurant', 'hotel', 'gas station', 'store', 'hospital'];
    const proximity = `${lon},${lat}`;
    const bbox = this.getBoundingBox(lat, lon, 3); // 3 mile radius

    try {
      // Search for each category and combine results
      const searchPromises = searchTerms.slice(0, 5).map(async (term) => {
        const searchUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(term)}.json?proximity=${proximity}&bbox=${bbox}&types=poi&limit=3&access_token=${MAPBOX_TOKEN}`;
        
        try {
          const response = await fetch(searchUrl);
          if (!response.ok) return [];
          
          const data = await response.json();
          return (data.features || []).map((feature: any) => {
            const [poiLon, poiLat] = feature.center || [lon, lat];
            const distance = this.calculateDistance(lat, lon, poiLat, poiLon);
            
            // Extract category from properties or determine from search term
            let category = feature.properties?.category || term;
            if (feature.properties?.maki) {
              category = feature.properties.maki;
            }
            
            return {
              name: feature.text || feature.place_name?.split(',')[0] || 'Unknown',
              category: category,
              address: feature.place_name,
              distance: Math.round(distance),
              latitude: poiLat,
              longitude: poiLon,
            };
          });
        } catch {
          return [];
        }
      });

      const results = await Promise.all(searchPromises);
      
      // Flatten and deduplicate by name
      const seen = new Set<string>();
      for (const categoryResults of results) {
        for (const place of categoryResults) {
          const key = `${place.name}-${place.latitude.toFixed(4)}`;
          if (!seen.has(key)) {
            seen.add(key);
            places.push(place);
          }
        }
      }
    } catch (e) {
      console.warn('POI search error:', e);
    }

    // Sort by distance and limit
    return places.sort((a, b) => (a.distance || 0) - (b.distance || 0)).slice(0, limit);
  }

  /**
   * Get bounding box for a given center point and radius
   */
  private getBoundingBox(lat: number, lon: number, radiusMiles: number): string {
    const latChange = radiusMiles / 69; // ~69 miles per degree latitude
    const lonChange = radiusMiles / (69 * Math.cos(lat * Math.PI / 180));
    
    const minLon = lon - lonChange;
    const minLat = lat - latChange;
    const maxLon = lon + lonChange;
    const maxLat = lat + latChange;
    
    return `${minLon},${minLat},${maxLon},${maxLat}`;
  }

  /**
   * Get comprehensive location context (address + nearby places)
   */
  async getLocationContext(lat: number, lon: number): Promise<LocationContext> {
    console.log(`[AreaIntel] Getting location context for (${lat}, ${lon})`);
    console.log(`[AreaIntel] Mapbox token configured: ${!!MAPBOX_TOKEN}`);
    
    const [geocodeResult, nearbyPlaces] = await Promise.all([
      this.reverseGeocode(lat, lon),
      this.getNearbyPOIs(lat, lon, undefined, 8)
    ]);

    console.log(`[AreaIntel] Geocode result:`, geocodeResult);
    console.log(`[AreaIntel] Found ${nearbyPlaces.length} nearby places:`, nearbyPlaces);

    return {
      ...geocodeResult,
      nearbyPlaces
    };
  }

  /**
   * Calculate distance between two points in meters (Haversine formula)
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Earth radius in meters
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) * 
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  /**
   * Get incident icon based on type
   */
  getIncidentIcon(type: string): string {
    const icons: Record<string, string> = {
      accident: '🚗💥',
      construction: '🚧',
      congestion: '🚦',
      road_closure: '🚫',
      weather: '⛈️',
      other: '⚠️'
    };
    return icons[type] || '⚠️';
  }

  /**
   * Get severity color
   */
  getSeverityColor(severity: string): string {
    const colors: Record<string, string> = {
      minor: '#4CAF50',
      moderate: '#FF9800',
      major: '#f44336',
      severe: '#9C27B0'
    };
    return colors[severity] || '#888888';
  }
}

export const areaIntelService = new AreaIntelService();
export default areaIntelService;
